// =============================================================================
//  Copyright : 2023 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : resolve wiki widgets/assets
// =============================================================================
// ---Third-party imports
import toposort from 'toposort';
// ---Framework imports
import log from 'uix/logging';
import { findElements } from 'uix/utils';
import { cloneDeep, dict, merge, items, isNil, flatten, groupBy, strip, callIfFunc } from 'ldash';
// ---Local imports
// ---Globals
let firstAssetNode = (dt, st)=>(assets)=>assets.filter((e)=>e.datatype == dt && (isNil(st) || e.subtype == st)).at(0);
let firstAssetId = (firstNodeFunc)=>(assets)=>{
        let pageNode = firstNodeFunc(assets);
        return pageNode ? pageNode.otlink || pageNode.otid : null;
    };
let firstPageNode = firstAssetNode(3);
let firstPageId = firstAssetId(firstPageNode);
let firstMdNode = firstAssetNode(1);
let firstMdId = firstAssetId(firstMdNode);
let firstWebImageNode = firstAssetNode(9, 2);
let firstWebImageId = firstAssetId(firstWebImageNode);
// port of python get_pagenode_image_source
let getImageSourceProps = (assets, resolveCaption = false)=>{
    let page_node = firstPageNode(assets);
    let props = {};
    if (page_node) {
        let crop = page_node.page.crop;
        if (crop) {
            if (resolveCaption && crop.title) {
                props.caption = crop.title;
            }
            props.imageSource = `/image/pagebox/${crop.opbid}/1000`;
        } else {
            props.imageSource = `/download/webcache/1000/${page_node.otlink || page_node.otid}`;
        }
    } else {
        props.imageSource = firstWebImageNode(assets) && `/download/webresource/${firstWebImageNode(assets).otid}`;
    }
    return props;
};
let resolveRules = {
    GraphicGroup: {
        children: ({ renderedChildren, assets })=>{
            let children = renderedChildren || [];
            let pageNodes = assets.filter((e)=>e.datatype == 3);
            let buildGraphic = (pageNode)=>{
                let g = {
                    $tag: 'Graphic',
                    id: pageNode.otid
                };
                if (pageNode.page.crop?.title) {
                    g = {
                        ...g,
                        caption: pageNode.page.crop?.title
                    };
                }
                return g;
            };
            children = [
                ...children,
                ...pageNodes.map(buildGraphic)
            ];
            return children;
        },
        attribs: ({ assets })=>{
            let linkid = firstMdId(assets);
            if (linkid) {
                return {
                    linkid
                };
            }
        }
    },
    Graphic: {
        attribs: ({ assets })=>{
            let res = {};
            let linkid = firstMdId(assets);
            if (linkid) {
                res = {
                    ...res,
                    linkid
                };
            }
            let pageNode = firstPageNode(assets);
            if (pageNode) {
                res = {
                    ...res,
                    id: pageNode.otid
                };
                if (!linkid) {
                    // prefer physical id for linking
                    res = {
                        ...res,
                        linkid: pageNode.otlink || pageNode.otid
                    };
                }
                if (pageNode.page.crop?.title) {
                    res = {
                        ...res,
                        caption: pageNode.page.crop?.title
                    };
                }
            }
            let webimageNode = firstWebImageNode(assets);
            if (webimageNode) {
                res = {
                    ...res,
                    id: webimageNode.otid
                };
            }
            return res;
        }
    },
    ImageViewer: {
        attribs: ({ assets })=>{
            let id = firstMdId(assets);
            if (id) {
                return {
                    id
                };
            }
            let pageNodes = assets.filter((e)=>e.datatype == 3);
            if (pageNodes.length) {
                let linkid = pageNodes[0].otid;
                return {
                    ids: pageNodes.map((n)=>n.otid),
                    linkid
                };
            }
        }
    },
    Quote: {
        attribs: ({ assets })=>{
            let mdNode = firstMdNode(assets);
            if (mdNode) {
                return {
                    linkid: mdNode.otlink || mdNode.otid,
                    caption: mdNode.caption
                };
            }
            let pageid = firstPageId(assets);
            if (pageid) {
                return {
                    linkid: pageid,
                    pageid
                };
            }
        }
    },
    Showcase: {
        attribs: ({ assets })=>{
            let id = firstMdId(assets);
            if (id) {
                return {
                    id
                };
            }
        }
    },
    Modal: {
        children: ({ renderedChildren })=>{
            let children = renderedChildren || [];
            if (children.length > 0) {
                children = [
                    {
                        $tag: 'ModalContent',
                        $children: children
                    }
                ];
            }
            return children;
        },
        attribs: ({ assets })=>{
            let pageNode = firstPageNode(assets);
            if (pageNode?.page.crop) {
                let bgid = pageNode.page.crop.opbid;
                return {
                    bgid
                };
            }
        }
    },
    TextTicker: {
        attribs: ({ assets, descendantAssets })=>{
            let attribs = {};
            let linkid = firstMdId(assets) || firstPageId(descendantAssets);
            if (linkid) {
                attribs = {
                    ...attribs,
                    linkid
                };
            }
            // XXX: should probably also be filtered on an audio subtype
            let audioAsset = firstAssetNode(9, 1)(assets);
            if (audioAsset) {
                attribs = {
                    ...attribs,
                    audio: `/download/webresource/${audioAsset.otid}`
                };
            }
            return attribs;
        }
    },
    TickerPage: {
        attribs: ({ assets })=>{
            let id = firstPageId(assets);
            if (id) {
                return {
                    id
                };
            }
        }
    },
    VideoPlayer: {
        attribs: ({ assets })=>{
            let attribs = {};
            // XXX: should probably also be filtered on a video subtype
            let asset = firstAssetNode(9, 1)(assets);
            if (asset) {
                attribs = {
                    ...attribs,
                    src: `/download/webresource/${asset.otid}`
                };
            }
            asset = firstAssetNode(9, 2)(assets);
            if (asset) {
                attribs = {
                    ...attribs,
                    poster: `/download/webresource/${asset.otid}`
                };
            }
            return attribs;
        }
    },
    AudioPlayer: {
        attribs: ({ assets })=>{
            // XXX: should probably also be filtered on an audio subtype
            let asset = firstAssetNode(9)(assets);
            if (asset) {
                return {
                    src: `/domainresource/${asset.otid}`
                };
            }
        }
    },
    AnnotationCanvas: {
        attribs: ({ assets })=>{
            // probably SVG, but raster images should also work
            let canvasAsset = firstAssetNode(9, 2)(assets);
            if (canvasAsset) {
                return {
                    canvasid: canvasAsset.otid
                };
            }
        }
    },
    MenuItem: {
        attribs: ({ assets })=>{
            let imageProps = getImageSourceProps(assets);
            return imageProps;
        }
    },
    TextBlock: {
        attribs: ({ assets })=>{
            let imageProps = getImageSourceProps(assets);
            return imageProps;
        }
    }
};
const topo_sort = (nodes, nodesById)=>{
    /* sort nodes by refid + parentid dependency
  - node -> ref1_node
  - node -> ref2_node
  - parent_node -> node
  */ let edges_refid = flatten(nodes.map((n)=>{
        let refEls = findElements(n.content?.rendered, ({ refid })=>refid);
        if (refEls.length > 0) {
            return refEls.filter((refEl)=>nodesById[refEl.refid]).map((refEl)=>[
                    n,
                    nodesById[refEl.refid]
                ]);
        }
    }).filter(Boolean));
    let edges_parent = nodes.filter((n)=>nodesById[n.parent]).map((n)=>[
            nodesById[n.parent],
            n
        ]);
    let edges = [
        ...edges_refid,
        ...edges_parent
    ];
    // in to-do order (stuff without dependencies first)
    return toposort.array(nodes, edges).reverse();
};
// map opr_tree record types to widget tags
// needed for creating tag names if widgets are not create/positioned with a @refid
const WIDGET_TAGS = {
    annotationcanvas: 'AnnotationCanvas',
    annotationitem: 'AnnotationItem',
    audio: 'AudioPlayer',
    card: 'Card',
    cards: 'Cards',
    column: 'Column',
    columns: 'Columns',
    focuspoint: 'FocusPoint',
    focuspointcontent: 'FocusPointContent',
    graphic: 'Graphic',
    graphicgroup: 'GraphicGroup',
    imageviewer: 'ImageViewer',
    menu: 'Menu',
    menuitem: 'MenuItem',
    modal: 'Modal',
    quote: 'Quote',
    showcase: 'Showcase',
    textticker: 'TextTicker',
    tickerpage: 'TickerPage',
    tile: 'Tile',
    tiles: 'Tiles',
    carousel: 'Carousel',
    carouselslide: 'CarouselSlide',
    transition: 'Transition',
    video: 'VideoPlayer'
};
// these tree node types will be scanned for children nodes which are auto-referenced (if empty content)
const TREE_RESOLVABLE = {
    annotationcanvas: true,
    cards: true,
    columns: true,
    focuspoint: true,
    graphic: true,
    graphicgroup: true,
    menu: true,
    modal: true,
    textticker: true,
    tiles: true,
    transition: true,
    carousel: true
};
// merge tree children, also if not empty (can contain <svg>)
const TREE_MERGE_ALLOWED = {
    annotationcanvas: true,
    focuspoint: true
};
const createReferencedWidgetTags = (treeChildren)=>treeChildren.filter((e)=>WIDGET_TAGS[e.type.name]).map((e)=>({
            $tag: WIDGET_TAGS[e.type.name],
            refid: e.otid
        }));
export const resolveWidgets = ({ nodes })=>{
    // will be updated with resolved nodes
    let nodesById = dict(nodes.map((n)=>[
            n.otid,
            n
        ]));
    // for child lookup
    let nodesByParent = groupBy(nodes, (e)=>e.parent);
    // process nodes in reversed topological dependency order (independent nodes first)
    let sorted_nodes = topo_sort(nodes, nodesById);
    for (let snode of sorted_nodes){
        let rootEl = snode.content?.rendered;
        // create refEls <XXX refid="YYY/> from opr_tree children
        // only resolve treeChildren if there is no wiki content
        // XXX: maybe merge into existing children in the future
        if (TREE_RESOLVABLE[snode.type.name]) {
            let allowedNotEmpty = TREE_MERGE_ALLOWED[snode.type.name];
            let isEmpty = !(rootEl?.$children?.length || strip(rootEl?.$text));
            if (isEmpty || allowedNotEmpty) {
                let treeChildren = nodesByParent[snode.otid] ?? [];
                let widgetChildren = createReferencedWidgetTags(treeChildren);
                if (widgetChildren.length > 0) {
                    let newChildren = [];
                    if (allowedNotEmpty && rootEl?.$children) {
                        // XXX: what about children.at(-1).tail
                        newChildren = [
                            ...rootEl?.$children
                        ];
                    }
                    rootEl = {
                        ...rootEl,
                        $children: [
                            ...newChildren,
                            ...widgetChildren
                        ]
                    };
                }
            }
        }
        // combine assets of children into parent node assets (recursivly due to topo sorted processing)
        let descendantAssets = flatten((nodesByParent[snode.otid] ?? []).map((n)=>nodesById[n.otid].descendantAssets || nodesById[n.otid].assets || []));
        if (snode.assets) {
            descendantAssets = [
                ...descendantAssets,
                ...snode.assets
            ];
        }
        snode = {
            ...snode,
            descendantAssets
        };
        // process referenced widgets
        let refEls = findElements(rootEl, ({ refid })=>refid);
        if (refEls.length > 0) {
            // something to do
            // clone for allowing mutations on refEl
            let nextRendered = cloneDeep(rootEl);
            // on cloned elements (mutate later)
            refEls = findElements(nextRendered, ({ refid })=>refid);
            for (let refEl of refEls){
                let refElTag = refEl.$tag;
                let sourceNode = nodesById[refEl.refid];
                if (!sourceNode) {
                    log.warn('%s: sourceNode %s not found', refElTag, refEl.refid);
                    continue;
                }
                // move refid attribute to "resolved_refid"
                refEl.resolved_refid = refEl.refid;
                delete refEl.refid;
                // mutate inplace
                let text = sourceNode.content?.rendered?.$text;
                if (text) {
                    refEl.$text = text;
                }
                let clonedChildren = null;
                let renderedChildren = cloneDeep(sourceNode.content?.rendered?.$children);
                let children_rule = resolveRules[refElTag]?.children;
                if (children_rule) {
                    // overwrite / merge children by inspecting asset data
                    //~ let treeChildren = nodesByParent[sourceNode.otid] ?? []
                    let assets = sourceNode.assets ?? [];
                    clonedChildren = children_rule({
                        renderedChildren,
                        assets
                    });
                } else if (renderedChildren) {
                    clonedChildren = renderedChildren;
                }
                if (clonedChildren) {
                    refEl.$children = clonedChildren;
                }
                // widget tree node caption will be transfered to <Widget caption="XXX">
                if (sourceNode.caption) {
                    refEl['caption'] = sourceNode.caption;
                }
                // copy meta into attributes
                for (let [k, v] of items(sourceNode.meta)){
                    // XXX: really ignore null / undefined / empty string?
                    if (!isNil(v) && v !== '') {
                        refEl[k] = String(v);
                    }
                }
                let attrib_rule = resolveRules[refElTag]?.attribs;
                if (attrib_rule) {
                    // overwrite attributes by inspecting asset data
                    let attribs = attrib_rule({
                        assets: sourceNode.assets || [],
                        descendantAssets: sourceNode.descendantAssets || []
                    });
                    for (let [k, v] of items(attribs)){
                        refEl[k] = String(v);
                    }
                }
            }
            // maybe copy more stuff (updateTS) from sourceNode
            snode = {
                ...snode,
                content: {
                    ...snode.content,
                    rendered: nextRendered
                }
            };
        }
        // next resolved nodes will read resolved node data
        nodesById[snode.otid] = snode;
    }
    // restore original order and return resolved nodes
    return nodes.map((n)=>nodesById[n.otid]);
};
